import { ComponentType, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, inject } from '@angular/core';
import { DialogRef } from './dialog-ref';
import { take } from 'rxjs/operators';

/**
 * Configuration options for dialogs.
 */
export interface DialogConfig<T = unknown> {
  data?: T;
  panelClass?: string;
  hasBackdrop?: boolean;
  backdropClass?: string;
  closeOnBackdropClick?: boolean;
  closeOnEscKey?: boolean;
}

/**
 * Injection token for dialog data.
 */
export const DIALOG_DATA = new InjectionToken<unknown>('DIALOG_DATA');

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private overlay = inject(Overlay);
  private injector = inject(Injector);

  /**
   * Opens a custom component in an overlay dialog.
   * @param component The component to be displayed in the dialog.
   * @param config Optional dialog configuration.
   * @returns A reference to the dialog.
   */
  public open<T, R = void>(
    component: ComponentType<T>,
    config?: DialogConfig<T>,
  ): DialogRef<R> {
    // Default configuration
    const defaultConfig: DialogConfig<T> = {
      hasBackdrop: true,
      backdropClass: 'overlay-backdrop',
      panelClass: 'overlay-panel',
      closeOnBackdropClick: true,
      closeOnEscKey: true,
    };

    // Merge user configuration with defaults
    const finalConfig = { ...defaultConfig, ...config };

    // Define position strategy to center the dialog
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    // Create the overlay
    const overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: finalConfig.hasBackdrop,
      backdropClass: finalConfig.backdropClass,
      panelClass: finalConfig.panelClass,
      disposeOnNavigation: true,
    });

    // Create a DialogRef instance
    const dialogRef = new DialogRef<R>(overlayRef);

    // Handle backdrop click if enabled
    if (finalConfig.closeOnBackdropClick) {
      overlayRef.backdropClick().pipe(take(1)).subscribe(() => {
        dialogRef.close();
      });
    }

    // Handle ESC key press if enabled
    if (finalConfig.closeOnEscKey) {
      overlayRef.keydownEvents().pipe(take(1)).subscribe(event => {
        if (event.key === 'Escape') {
          dialogRef.close();
        }
      });
    }

    // Create an injector for providing dependencies to the component
    const injector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: DialogRef, useValue: dialogRef },
        { provide: DIALOG_DATA, useValue: finalConfig.data },
      ],
    });

    // Attach the component to the overlay using a portal
    const portal = new ComponentPortal(component, null, injector);
    overlayRef.attach(portal);

    return dialogRef;
  }
}
